import { Component, EventEmitter, Output, ViewChild, OnInit, OnDestroy, Input } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/shared/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'atom-multiple-dates',
  templateUrl: './multiple-dates.component.html',
  styleUrls: ['./multiple-dates.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-CO' },
  ],
})
export class MultipleDatesComponent implements OnInit, OnDestroy {
  maxDate: Date = new Date();
  dateControl!: FormControl;

  // Bloquea la selección de fechas futuras
  public disableFutureDates = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date <= today;
  }

  public init = new Date();
  public dateValue = new Date(0);
  public arrayDates: Date[] = [];
  public closeOnSelected = false;
  public currentLanguage: string = '';
  translatedTitle: string = '';
  @Input() title: string = 'MULTIPLE-DATES.Fechas';

  @ViewChild('picker', { static: true }) _picker!: MatDatepicker<Date>;
  @Output() changed = new EventEmitter<Array<Date>>();

  private langSubscription: Subscription | undefined;
  private monthAbbreviations: { [key in 'en' | 'es']: string[] } = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  };
  private translateSubscription?: Subscription;

  constructor(
    private adapter: DateAdapter<any>,
    private languageService: LanguageService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    // Inicializar el FormControl con validaciones
    this.dateControl = new FormControl(null, [Validators.max(this.maxDate.getTime())]);

    // Configuración inicial del idioma
    this.currentLanguage = this.languageService.getCurrentLanguage();
    this.setLocale(this.currentLanguage);
    this.updateTitleTranslation();

    // Suscribirse a cambios en el idioma
    this.langSubscription = this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang;
      this.adapter.setLocale(lang);
      this.updateTitleTranslation();
    });
    this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateTitleTranslation();
    });
  }

  ngOnDestroy(): void {
    // Cancelar suscripciones
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }

  private updateTitleTranslation() {
    if (this.title && this.title.trim() !== '') {
      this.translateService.get(this.title).subscribe(translation => {
        this.translatedTitle = translation;
      });
    }
  }

  private setLocale(lang: string): void {
    this.adapter.setLocale(lang); // Actualizar el idioma del adaptador de fechas
  }

  public dateChanged(event: MatDatepickerInputEvent<any>): void {
    if (event.value) {
      // Convertir a Date si es moment
      const date: Date = moment.isMoment(event.value) ? event.value.toDate() : event.value;
      const index = this.getDateIndex(date);

      if (index === -1) {
        this.arrayDates.unshift(date);
        this.changed.emit(this.arrayDates);
      } else {
        this.arrayDates.splice(index, 1);
      }
      
      // Resetear el FormControl en lugar de dateValue
      this.dateControl.setValue(null);

      if (!this.closeOnSelected) {
        const closeFn = this._picker.close;
        this._picker.close = () => {};
        this._picker['_componentRef'].instance._calendar.monthView._createWeekCells();
        setTimeout(() => {
          this._picker.close = closeFn;
        });
      }
    }
  }

  public dateClass = (date: Date) => {
    if (this.getDateIndex(date) !== -1) {
      return ['selected'];
    }
    return [];
  };

  public removeDate(date: Date): void {
    const index = this.getDateIndex(date);
    this.arrayDates.splice(index, 1);
  }

  private getDateIndex(date: any): number {
    // Convertir a Date si es moment
    const d = moment.isMoment(date) ? date.toDate() : date;
    return this.arrayDates.map((m) => +m).indexOf(+d);
  }

  formatDate(date: Date): string {
    const monthAbbr = this.monthAbbreviations[this.currentLanguage as keyof typeof this.monthAbbreviations][date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${monthAbbr} ${day}, ${year}`;
  }

  // Método para resetear las fechas seleccionadas
  reset(): void {
    this.arrayDates = [];
    this.dateControl.setValue(null);
    this.changed.emit(this.arrayDates);
  }
}
